<template>
  <div>
    <div class="stat-filter">
      <RowFilters :filters="defaultFilters" @change="onFiltersChange" />
      <div class="stat-filter--second-row">
        <div class="d-flex">
          <button @click="openModal" class="btn mr-15 btn-sm">
            <span class="icon-lines">|||</span>
          </button>
          <ChoiceRange :pickerEnabled="true" :tz="activeFilters.dates.timezone" @change="onDatesChange" />
          <EnableRemoteStatsCheckbox v-model="enableRemoteStats" />
        </div>
        <div class="stat-filter--submit">
          <span class="btn stat-filter--clear-btn" @click="clearFilters">Очистить</span>
          <button class="btn stat-filter--submit-btn" type="button" @click="applyFilters">Применить</button>
        </div>
      </div>
    </div>
    <div v-if="feedsWithoutApi.length > 0 || apiErrors.length > 0" class="stat-warnings">
      <div v-if="apiErrors.length > 0" class="stat-warnings--errors">
        ⚠ API-статистика фидов неполная, «Доход (по фиду)», «Дискреп» и «Маржа (по фиду)» могут быть занижены:
        <ul>
          <li v-for="err in apiErrors" :key="err">{{ err }}</li>
        </ul>
      </div>
      <div v-if="feedsWithoutApi.length > 0">Фиды без API статистики (не входят в «по фиду»): {{ feedsWithoutApi.join(', ') }}</div>
    </div>
    <div class="table-container">
      <table-v1 :columns="columns" :rows="rows" :exports="true" :exportsFilename="exportsFilename"></table-v1>
    </div>
    <source-column-modal @toggleAll="toggleAll" :toggleSelectOnce="toggleSelectOnce" :columns="columns" v-model="open" @close="close" />
  </div>
</template>

<script>
import ChoiceRange from '@/components/ChoiceRange';
import EnableRemoteStatsCheckbox from '@/components/EnableRemoteStatsCheckbox.vue';
import RowFilters from '@/components/RowFilters';
import SourceColumnModal from '@/components/sourceColumnModal.vue';
import TableV1 from '@/components/TableV1';
import { useEnableRemoteStats, usePreferedTimezone } from '@/utils';
import Vue from 'vue';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/ru';

const timezone = usePreferedTimezone();
const enableRemoteStats = useEnableRemoteStats('stats');

export default {
  name: 'Statistics',
  components: {
    EnableRemoteStatsCheckbox,
    TableV1,
    ChoiceRange,
    RowFilters,
    SourceColumnModal,
  },
  data: function() {
    return {
      open: false,
      defaultFilters: [],
      activeFilters: {
        groupBy: 'day',
        dates: {
          from: new Date(),
          to: new Date(),
          timezone: timezone,
        },
      },
      rows: [],
      warnings: [],
      // вынул из computed columns
      columns: [
        {
          label: 'Дата',
          field: 'date',
          type: 'date',
          dateInputFormat: 'yyyy-MM-dd',
          dateOutputFormat: 'dd-MM-yyyy',
          hidden: false,
          checked: true,
          disabled: true,
          showWhen: (row) => typeof row.date !== 'undefined',
        },
        {
          label: 'Час',
          field: 'hour',
          type: 'number',
          hidden: false,
          checked: true,
          showWhen: (row) => typeof row.hour !== 'undefined',
        },
        {
          label: 'Фид',
          field: 'feed',
          type: 'number',
          hidden: false,
          checked: true,
          showWhen: (row) => typeof row.feed !== 'undefined',
        },
        {
          label: 'Страна',
          field: 'country',
          hidden: false,
          checked: true,
          showWhen: (row) => typeof row.country !== 'undefined',
        },
        {
          label: 'Браузер',
          field: 'browser',
          hidden: false,
          checked: true,
          showWhen: (row) => typeof row.browser !== 'undefined',
        },
        {
          label: 'Система',
          field: 'os',
          hidden: false,
          checked: true,
          showWhen: (row) => typeof row.os !== 'undefined',
        },
        {
          label: 'Тип',
          field: 'type',
          hidden: false,
          checked: true,
          showWhen: (row) => typeof row.type !== 'undefined',
        },
        {
          label: 'Пользователь',
          field: 'user',
          hidden: false,
          checked: true,
          showWhen: (row) => typeof row.user !== 'undefined',
        },
        {
          label: 'Прощадка',
          field: 'site',
          hidden: false,
          checked: true,
          showWhen: (row) => typeof row.site !== 'undefined',
        },
        {
          label: 'Устройство',
          field: 'device',
          hidden: false,
          checked: true,
          showWhen: (row) => typeof row.device !== 'undefined',
        },
        {
          label: 'Запросы',
          field: 'requests',
          tooltip: 'Входящие запросы в Feed.House.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatNumber,
          showWhen: (row) => typeof row.feed === 'undefined',
        },
        {
          label: 'Ответы',
          field: 'filledRequests',
          tooltip: 'Входящие запросы, на которые мы отдали рекламу.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatNumber,
          showWhen: (row) => typeof row.feed === 'undefined',
        },
        {
          label: 'FillRate',
          field: 'covering',
          tooltip: 'Ответы / Запросы: доля входящих запросов, на которые отдали рекламу.', // TODO: rename to "feedHouseFillRate"
          type: 'percentage',
          formatFn: (value) => this.$options.filters.formatPercent(value, 2),
          hidden: false,
          checked: true,
          showWhen: (row) => typeof row.feed === 'undefined',
        },
        {
          label: 'Запросы на фиды',
          field: 'feedRequests',
          tooltip: 'Запросы, которые мы отправили в фиды (на один входящий запрос их может быть несколько).',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatNumber,
        },
        {
          label: 'Ответы с фидов',
          field: 'feedResponses',
          tooltip: 'Ответы фидов с рекламой.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatNumber,
        },
        {
          label: 'Пустые ответы с фидов',
          field: 'feedEmptyResponses',
          tooltip: 'Ответы фидов без рекламы.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatNumber,
        },
        {
          label: 'Ошибки',
          field: 'feedErrors',
          tooltip: 'Ошибки и таймауты фидов.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatNumber,
        },
        {
          label: 'FillRate',
          field: 'fillRate',
          tooltip: 'Ответы с фидов / Запросы на фиды.', // TODO: rename to "feedsFillRate"
          type: 'percentage',
          formatFn: (value) => this.$options.filters.formatPercent(value, 2),
          hidden: false,
          checked: true,
        },
        {
          label: 'Показы',
          field: 'impressions',
          tooltip: 'Наши показы рекламы фидов.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatNumber,
        },
        {
          label: 'Клики',
          field: 'totalClicks',
          tooltip: 'Наши клики по рекламе фидов (без отброшенных).',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatNumber,
        },
        {
          label: 'Лиды',
          field: 'tbLeads',
          tooltip: 'CPA-лиды: конверсии из кликов, по которым оффер прислал постбек.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatNumber,
        },
        {
          label: 'CTR',
          field: 'ctr',
          tooltip: 'Клики / Показы.',
          type: 'percentage',
          formatFn: (value) => this.$options.filters.formatPercent(value, 2),
          hidden: false,
          checked: true,
        },
        {
          label: 'Средний CPC',
          field: 'cpcAvg',
          tooltip: 'Доход за клики (без CPA-лидов) / Клики.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: (val) => this.$options.filters.formatMoneyWithFraction(val, 5),
        },
        {
          label: 'Средняя цена фида',
          field: 'bidAvg',
          tooltip: 'Средняя цена в ответах фидов.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: (val) => this.$options.filters.formatMoneyWithFraction(val, 5),
        },
        {
          label: 'WinRate',
          field: 'winRate',
          tooltip: 'Показы / Запросы на фиды.',
          type: 'percentage',
          formatFn: (value) => this.$options.filters.formatPercent(value, 2),
          hidden: false,
          checked: true,
        },
        {
          label: 'Доход $ (наш подсчёт)',
          field: 'sendMoney',
          tooltip: 'Сколько фид должен нам заплатить по НАШИМ данным: цена фида за наши клики + выплаты за CPA-лиды. Выплаты вебмастерам здесь не вычитаются.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatMoney,
        },
        {
          label: 'Доход $ (по фиду)',
          field: 'getMoney',
          tooltip: 'Сколько фид начислил нам по СВОЕМУ API. Если отличается от «Доход (наш подсчёт)» — это денежный дискреп.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatMoney,
        },
        {
          label: 'Дискреп $',
          field: 'difference',
          tooltip: 'Доход по фиду − Доход по нашему подсчёту тех же фидов (только фиды с API статистики). Минус: фид засчитал меньше, чем мы ожидали. Это не наша маржа.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatMoneyWithSign,
        },
        {
          label: 'Выплаты вебам $',
          field: 'webmasterProfit',
          tooltip: 'Сколько мы платим вебмастерам: revshare за клики и CPA-лиды. CPL-вебам платим за подписки, а не за клики, их выплат здесь нет — маржа на их трафике завышена.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatMoney,
        },
        {
          label: 'Маржа $ (наш подсчёт)',
          field: 'marginOur',
          tooltip: 'Доход (наш подсчёт) − Выплаты вебам. Сколько остаётся нам по НАШИМ данным.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: this.$options.filters.formatMoney,
        },
        {
          label: 'Маржа $ (по фиду)',
          field: 'marginFeed',
          tooltip: 'Доход (по фиду) − Выплаты вебам. Сколько остаётся нам по данным фида. Учитываются только фиды с API статистики: фиды без API не входят ни в доход, ни в выплаты. Пусто, если API-статистики нет для этой группировки или фильтров.',
          type: 'number',
          hidden: false,
          checked: true,
          formatFn: (val) => (val === null ? '—' : this.$options.filters.formatMoney(val)),
        },
      ],
      enableRemoteStats: enableRemoteStats,
    };
  },

  created() {
    Vue.prototype.$reload = () => this.fetchData();
    this.fetchData();
    this.setStatisticsFromStorage();
  },
  computed: {
    // Feeds that don't provide a stats API, expected and not an error.
    feedsWithoutApi() {
      return this.warnings
        .map(w => w.match(/^no stats client found for feed (.+) \(id=\d+\)$/))
        .filter(Boolean)
        .map(m => m[1]);
    },
    apiErrors() {
      return this.warnings.filter(w => !w.startsWith('no stats client found for feed '));
    },
  },
  methods: {
    toggleAll(evt) {
      if (evt) {
        this.columns.forEach((e) => e.checked = true);
        this.columns.forEach((e) => e.hidden = false);
      } else {
        this.columns.forEach((e) => e.field !== 'date' ? e.checked = false : e.checked = true);
        this.columns.forEach((e) => e.field !== 'date' ? e.hidden = true : e.hidden = false);
      }
    },
    toggleSelectOnce(evt) {
      evt.hidden = !evt.checked;
    },
    setStatisticsFromStorage() {
      const getStorage = localStorage.getItem('statisticsLocal');
      this.columns.forEach((e) => {
        if (getStorage) {
          if (getStorage.includes(e.field)) {
            e.hidden = false;
            e.checked = true;
          } else {
            e.hidden = true;
            e.checked = false;
          }
        }
      });
    },
    close(evt) {
      const arr = [];
      this.columns.map((e) => {
        if (!e.hidden) {
          arr.push(e.field);
        }
      });
      if (evt) {
        localStorage.setItem('statisticsLocal', arr.toString());
      }
      this.open = false;
    },
    openModal() {
      this.open = true;
    },
    async fetchData() {
      try {
        this.$globalSpinner.show();

        const filters = { ...this.activeFilters };

        const statsFilters = {
          from: this.$options.filters.formatDateYmd(filters.dates.from),
          to: this.$options.filters.formatDateYmd(filters.dates.to),
          timezone: filters.dates.timezone,
          disableRemoteStats: !this.enableRemoteStats,
        };

        delete filters.dates;

        Object.keys(filters).forEach((key) => {
          statsFilters[key] = filters[key];
        });

        const {
          data: stats,
          filters: possibleFilters,
          warnings = [],
        } = await this.$api.getStatistics(statsFilters);
        this.warnings = warnings;

        this.updateFilterValues(possibleFilters);

        this.rows = stats.map((item) => ({
          date: item.date ? this.$options.filters.formatDateYmd(new Date(item.date)) : undefined,
          hour: item.hour,
          feed: item.feed,
          country: item.country,
          browser: item.browser,
          os: item.os,
          type: item.type,
          user: item.user,
          site: item.site,
          device: item.device,

          requests: item.requests,
          filledRequests: item.filledRequests,
          covering: item.covered,
          feedRequests: item.feedRequests,
          feedResponses: item.feedResponses,
          feedEmptyResponses: item.feedEmptyResponses,
          feedErrors: item.feedErrors,
          fillRate: item.fillRate,
          impressions: item.impressions,
          clicks: item.clicks,
          tbLeads: item.tbLeads,
          totalClicks: item.totalClicks,
          ctr: item.ctr,
          sendMoney: item.sent,
          getMoney: item.received,
          difference: item.difference,
          webmasterProfit: item.webmasterProfit,
          marginOur: item.margin,
          marginFeed: item.marginReceived,
          winRate: item.winRate,
          cpcAvg: item.cpcAvg,
          bidAvg: item.bidAvg,
        }));
      } catch (_) {
        this.$notification.error('Ошибка загрузки статистики');
      } finally {
        this.$globalSpinner.hide();
      }
    },
    updateFilterValues(possibleFilters) {
      this.defaultFilters = possibleFilters.map(filter => {
        return {
          id: filter.id,
          defaultValue: filter.values[0].value,
          items: filter.values,
        };
      });
    },
    clearFilters() {
      this.activeFilters = {
        groupBy: 'day',
        dates: {
          from: new Date(),
          to: new Date(),
        },
      };
      // this.applyFilters();
    },
    applyFilters() {
      this.fetchData();
    },
    // it's called when dates changed
    onDatesChange([from, to, timezone]) {
      this.activeFilters.dates.from = from;
      this.activeFilters.dates.to = to;
      this.activeFilters.dates.timezone = timezone;
    },
    // it's called when dropdowns changed
    onFiltersChange(filters) {
      this.activeFilters = {
        ...this.activeFilters,
        ...filters,
      };
    },
    exportsFilename() {
      const parts = [
        'feedhouse-stats',
        this.activeFilters.groupBy,
        this.activeFilters.dates.from.toLocaleDateString(),
        this.activeFilters.dates.to.toLocaleDateString(),
        this.activeFilters.dates.timezone,
      ];

      return parts.join('_');
    },
  },
};
</script>

<style scoped>
.stat-warnings {
  margin: 10px 0;
  padding: 10px 15px;
  font-size: 14px;
  color: #666;
}

.stat-warnings--errors {
  color: #e65100;
}

.stat-warnings ul {
  margin: 5px 0 5px 20px;
}
</style>
